import { useTranslation } from 'react-i18next';
import { AccountData, OrderData, OrderService } from '../../../api';
import {
  calculateRequiredResourceCount,
  calculateResourceSelection,
} from '../../dialogs/EditOrderDialog/components/resourceSelectionPanel/resourceSelectionUtils';
import { ApiResponseHandlerType } from '../../../common/apiNotifications/ApiResponseHandlerContext';
import {
  getOrderSchedulerConfig,
  ResourceValidationErrorType,
  validateResourceAssignment,
} from './resourceValidationUtils';
import {
  isResourceDropTarget,
  isUnassignedDropTarget,
  ResourceDropTarget,
  UnassignedDropTarget,
} from './DragDropTypes';
import { useAssignmentConflictHandler } from '../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useApiResponseHandlerContext } from '../../../common/apiNotifications/useApiResponseHandlerContext';

/**
 * Find the default scheduler config for an order based on its order page ID
 * This mimics the backend logic: SchedulerConfigManager.find_scheduler_config_by_order_page_id
 */
const findDefaultSchedulerConfigForOrder = (order: OrderData, account: AccountData): string | null => {
  const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];

  // Find the config that has this order page in its allowed masks
  const defaultConfig = schedulerConfigs.find((config) =>
    config.allowed_masks?.some((mask) => mask.order_page_id === order.order_page_id),
  );

  return defaultConfig?.id || null;
};

/**
 * Determine the override scheduler config ID, mimicking backend logic:
 * Only set as override if different from order page's default config
 */
const getSchedulerOverrideIfNeeded = (
  order: OrderData,
  targetSchedulerConfigId: string | undefined,
  account: AccountData,
): string | undefined => {
  if (!targetSchedulerConfigId) {
    return undefined;
  }

  const defaultConfigId = findDefaultSchedulerConfigForOrder(order, account);

  // Only set override if target is different from default
  return targetSchedulerConfigId === defaultConfigId ? undefined : targetSchedulerConfigId;
};

const calculateDragResourceSelection = (
  order: OrderData,
  targetResourceIndex: number,
  targetSchedulerConfigId: string,
  account: AccountData,
): number[] => {
  const config = getOrderSchedulerConfig(order, account);
  if (!config || config.id !== targetSchedulerConfigId) {
    // Fallback to single resource if config mismatch
    return [targetResourceIndex];
  }

  const persons = order.persons || 1;

  // Get all available resource indexes for this config (simplified - assumes all are available for drag)
  const availableResourceIndexes = Array.from({ length: config.resource_count }, (_, i) => i);

  const calculation = calculateResourceSelection(targetResourceIndex, persons, config, availableResourceIndexes);

  if (calculation.isValid && calculation.selectedResources) {
    return calculation.selectedResources;
  }

  // Fallback to single resource if calculation fails
  return [targetResourceIndex];
};

type HandleResourceDropProps = {
  order: OrderData;
  dropTarget: ResourceDropTarget | UnassignedDropTarget;
  updateOrder: (order: OrderData, revalidate?: boolean) => void;
};

export const useHandleResourceDrop = () => {
  const { t } = useTranslation();
  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { showResponseNotification } = useApiResponseHandlerContext();

  return async ({ order, dropTarget, updateOrder }: HandleResourceDropProps): Promise<void> => {
    // Determine resource IDs based on drop target
    let resourceIndexes: number[] | undefined;
    let schedulerConfigId: string | undefined;
    let validation: any = { isValid: true }; // Default to valid for unassigned case

    if (isResourceDropTarget(dropTarget)) {
      // Calculate multi-resource selection based on order requirements
      const targetResourceIndex = dropTarget.resourceIndex - 1; // Convert from 1-based to 0-based
      resourceIndexes = calculateDragResourceSelection(
        order,
        targetResourceIndex,
        dropTarget.schedulerConfigId,
        account,
      );

      // Always send the target scheduler config - let backend handle assignment
      schedulerConfigId = dropTarget.schedulerConfigId;

      // Client-side validation before API call
      validation = validateResourceAssignment(
        order,
        targetResourceIndex,
        dropTarget.schedulerConfigId,
        resourceIndexes,
        account,
      );

      if (!validation.isValid) {
        // Show validation error and return early (only for capacity issues)
        let errorMessage: string;
        const config = account?.shared_resources?.scheduler_configs?.find((c) => c.id === dropTarget.schedulerConfigId);
        switch (validation.errorType) {
          case ResourceValidationErrorType.INSUFFICIENT_SPACE:
            errorMessage = t('calendar.drag_resource.error_insufficient_space', {
              resourcesNeeded: calculateRequiredResourceCount(order.persons || 1, config?.persons_per_resource || 1),
              availableSpace: Math.max(0, (config?.resource_count || 0) - targetResourceIndex),
            });
            break;
          default:
            errorMessage = validation.errorMessage || t('calendar.drag_resource.error_generic');
        }

        showResponseNotification(errorMessage, ApiResponseHandlerType.ERROR);
        console.warn('Resource assignment validation failed:', validation);
        return;
      }

      // Show warning banner if there are warnings but allow the operation to proceed
      if (validation.warningMessage) {
        let warningMessage: string;
        switch (validation.warningType) {
          case ResourceValidationErrorType.WRONG_GROUP:
            warningMessage = `${t('calendar.drag_resource.warning_wrong_group')}`;
            break;
          case ResourceValidationErrorType.MASK_NOT_ALLOWED:
            warningMessage = `${t('calendar.drag_resource.warning_mask_not_allowed')}`;
            break;
          default:
            warningMessage = `${validation.warningMessage}`;
        }

        showResponseNotification(warningMessage, ApiResponseHandlerType.WARNING);
        console.warn('Resource assignment warning:', validation);
      }
    } else {
      // Unassigned - empty array to clear assignment and clear scheduler config
      resourceIndexes = [];
      schedulerConfigId = undefined;
    }

    const hasWarnings = !!validation.warningMessage;

    // Only set override_scheduler_config_id if different from the order's default scheduler config
    const overrideSchedulerConfigId = getSchedulerOverrideIfNeeded(order, schedulerConfigId, account);
    updateOrder(
      {
        ...order,
        resource_indexes: resourceIndexes,
        override_scheduler_config_id: overrideSchedulerConfigId,
      },
      false,
    ); // false = don't revalidate yet
    const updatedOrder = await executeWithConflictCheck(
      () =>
        OrderService.assignOrderToResource({
          order_id: order.id,
          resource_indexes: resourceIndexes,
          scheduler_config_id: schedulerConfigId,
        }),
      isUnassignedDropTarget(dropTarget)
        ? t('calendar.assign_resource.successRemove')
        : t('calendar.assign_resource.success'),
      undefined, // finallyCallback
      hasWarnings, // suppressSuccessMessage when we have warnings
    );
    // calling twice so there is revalidation in case wasn't successfull
    updateOrder(updatedOrder);
  };
};
